import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Stack, Text } from '@chakra-ui/react';

import LearnerDashboardContentListItem from '@/client/components/learners/dashboard/components/lists/LearnerDashboardContentListItem';
import type { UserAssignment } from '@/client/types/dashboard/UserAssignment';
import { format } from 'date-fns';
import { setContentUrl } from '@/client/components/learners/dashboard/utils/setContentUrl';
import { useLearnerDashboard } from '@/client/components/learners/dashboard/hooks/useLearnerDashboard';
import { useSetCompletionStatus } from '@/client/components/learners/dashboard/hooks/useSetCompletionStatus';
import { useTranslation } from 'react-i18next';

export default function GroupedAssignmentDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setCompletionStatus } = useSetCompletionStatus();
  const { contentTypeTranslationMap } = useLearnerDashboard();
  const assignment = location.state?.assignment as UserAssignment;

  const openContent = assignment?.assigned_content.filter(
    (content) =>
      !assignment.completed_content.some((oc) => oc.content_id === content.content_id._id),
  );

  const completedContent = assignment?.assigned_content.filter((content) =>
    assignment.completed_content.some((oc) => oc.content_id === content.content_id._id),
  );

  if (!assignment) return <Navigate to="/dashboard" replace />;

  return (
    <Stack spacing={4}>
      <Text fontSize="24px" fontWeight={600} color="baseBlack">
        {t('learners.dashboard.assignment')}
        {assignment.due_date && (
          <Text as="span">
            -{' '}
            {t('learners.dashboard.assignment.dueDate', {
              date: format(new Date(assignment.due_date), 'MMM d, yyyy'),
            })}
          </Text>
        )}
        {!assignment.due_date && (
          <Text as="span">- {t('learners.dashboard.assignment.noDueDate')}</Text>
        )}
      </Text>

      <Stack spacing={6}>
        <Text fontSize="20px" fontWeight={600} color="baseBlack">
          {t('learners.dashboard.assignments.itemsToDo', {
            count: openContent.length,
          })}
        </Text>

        {openContent.map((content, index) => {
          const imageUrl = content.content_id.images?.explore || content.content_id.image;

          if (!content.content_id || content.content_id.archived) return;

          return (
            <LearnerDashboardContentListItem
              key={content.user_content_id}
              title={content.content_id.title || content.content_id.name}
              typeLabel={contentTypeTranslationMap[content.content_id.type]}
              renderBottomBorder={index !== openContent.length - 1}
              imageUrl={imageUrl}
              statusData={setCompletionStatus(assignment)}
              onClick={() =>
                navigate(
                  setContentUrl(
                    content.content_id._id,
                    content.content_id.type,
                    content.user_content_id,
                  ),
                )
              }
            />
          );
        })}
      </Stack>

      <Stack spacing={6}>
        <Text fontSize="20px" fontWeight={600} color="baseBlack">
          {t('learners.dashboard.assignments.completedItems')}
        </Text>

        {completedContent.map((content, index) => {
          const imageUrl = content.content_id.images?.explore || content.content_id.image;

          if (!content.content_id || content.content_id.archived) return;

          return (
            <LearnerDashboardContentListItem
              key={content.user_content_id}
              title={content.content_id.title || content.content_id.name}
              typeLabel={contentTypeTranslationMap[content.content_id.type]}
              renderBottomBorder={index !== completedContent.length - 1}
              imageUrl={imageUrl}
              statusData={undefined}
              onClick={() =>
                navigate(
                  setContentUrl(
                    content.content_id._id,
                    content.content_id.type,
                    content.user_content_id,
                  ),
                )
              }
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
